import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlacements } from 'core/actions/ocapi';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'core/utils/dayjs';

import { runTimer } from 'core/utils/helpers/date';

import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import Countdown from 'core/modules/Countdown/Countdown';
import HtmlText from 'core/modules/HtmlText/HtmlText';
import BonusTerms from 'core/modules/BonusTerms/BonusTerms';
import HeadInfo from './components/HeadInfo/HeadInfo';

import {
  selectMarketingTextConfig, makeSelectMarketingTextData, makeSelectMarketingTextIsLoading,
  makeSelectMarketingTextIsLoaded, makeSelectMarketingTextResponseDate,
} from './selectors';

import './MarketingText.scss';

function MarketingText({ include_bonus_details: hasBonus, content_group_id, namespace }) {
  const selectMarketingTextData = makeSelectMarketingTextData();
  const selectMarketingTextIsLoading = makeSelectMarketingTextIsLoading();
  const selectMarketingTextIsLoaded = makeSelectMarketingTextIsLoaded();
  const selectMarketingTextResponseDate = makeSelectMarketingTextResponseDate();

  const config = useSelector(selectMarketingTextConfig);
  const isLoading = useSelector(state => selectMarketingTextIsLoading(state, namespace));
  const isLoaded = useSelector(state => selectMarketingTextIsLoaded(state, namespace));
  const data = useSelector(state => selectMarketingTextData(state, namespace));
  const currentDate = useSelector(state => selectMarketingTextResponseDate(state, namespace));

  const dispatch = useDispatch();

  const [showCountdown, setShowCountdown] = useState(data.hasCountdown);
  const [isOfferActive, setIsOfferActive] = useState(true);

  const addCountDown = () => {
    setShowCountdown(true);
  };

  const removeCountDown = () => {
    setShowCountdown(false);
  };

  const removeInactiveOffer = () => {
    setIsOfferActive(false);
  };

  const countDownCompleteHandler = () => {
    const {
      countdownStopDatetime, stopDatetime,
    } = data || {};
    removeCountDown();

    const hasCustomCountdownStopDate = !(dayjs(countdownStopDatetime).isSame(stopDatetime));
    if (hasCustomCountdownStopDate) {
      runTimer({
        endDate: stopDatetime,
        onComplete: () => { removeInactiveOffer(); },
      });
    } else {
      removeInactiveOffer();
    }
  };

  const renderBonusTerms = () => {
    if (hasBonus === '1') {
      return <BonusTerms />;
    }

    return null;
  };

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      const { api: { options } } = config;
      const params = {
        content_group_id,
      };

      if (params.content_group_id) { // content_group_id param is required for /placements API endpoint
        dispatch(fetchPlacements(params, { ...options, namespace }));
      }
    }
  }, []);

  useEffect(() => {
    if (data.hasCountdown) {
      addCountDown();
    }
  }, [data.hasCountdown]);

  if (isEmpty(data)) {
    return renderBonusTerms();
  }

  const {
    assets, countdownStopDatetime, termsAndConditions, offerTitle,
  } = data;
  const { countdownClockPrefixText } = assets;
  const { textClockBar } = config;

  return (
    <ComponentErrorBoundary>
      {isEmpty(termsAndConditions) ?
        isOfferActive &&
          <div className="mn_marketingText" data-test="marketing-text">
            <HeadInfo {...data} />
            {showCountdown && (
              <div className="mn_countdownClockWrap">
                <span className="mn_countdownClockInnerWrap">
                  <p>{countdownClockPrefixText}<span className="mn_countdownClockTextBar">{textClockBar}</span></p>
                  <Countdown
                    currentDate={currentDate}
                    endDate={countdownStopDatetime}
                    onComplete={() => countDownCompleteHandler()}
                  />
                </span>
              </div>
            )}
          </div>
        : <HtmlText text={termsAndConditions} section_title={offerTitle} />
      }
      {renderBonusTerms()}
    </ComponentErrorBoundary>
  );
}

MarketingText.defaultProps = {
  namespace: 'default',
};

export default (props) => <ComponentErrorBoundary><MarketingText {...props} /></ComponentErrorBoundary>;
